<template>
  <figure class="base-chart" :class="{ 'base-chart--loading': loading }">
    <figcaption v-if="title || description || $slots['header-actions']" class="base-chart-header">
      <div class="base-chart-heading">
        <span v-if="title" class="base-chart-title">{{ title }}</span>
        <span v-if="description" class="base-chart-description">{{ description }}</span>
      </div>
      <div v-if="$slots['header-actions']" class="base-chart-header-actions">
        <slot name="header-actions" />
      </div>
    </figcaption>
    <div v-if="loading" class="base-chart-empty" role="status">
      <v-progress-circular indeterminate color="primary" :size="32" :width="3" />
    </div>
    <div v-else-if="isEmpty" class="base-chart-empty">
      <v-icon class="base-chart-empty-icon">mdi-chart-bar</v-icon>
      <p class="base-chart-empty-text">{{ emptyMessage }}</p>
    </div>
    <apexchart
      v-else
      :type="type"
      :series="apexSeries"
      :options="mergedOptions"
      :height="height"
      @dataPointSelection="onPointSelect"
    />
  </figure>
</template>

<script>
import { computed } from "vue";

const PRIMARY = "rgb(0, 109, 62)";
const PALETTE = [
  PRIMARY,
  "rgb(0, 85, 48)",
  "rgb(60, 145, 105)",
  "rgb(143, 188, 143)",
  "rgb(199, 234, 70)",
];

const isCategoricalType = (type) =>
  type === "donut" || type === "pie" || type === "radialBar";

export default {
  name: "BaseChart",
  props: {
    type: { type: String, default: "bar" },
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    // For donut/pie: a flat number array. For axis charts: an array of
    // { name, data } objects.
    series: { type: [Array], default: () => [] },
    categories: { type: Array, default: () => [] },
    height: { type: [Number, String], default: 320 },
    loading: { type: Boolean, default: false },
    emptyMessage: { type: String, default: "No data yet." },
    formatTooltip: { type: Function, default: null },
    colors: { type: Array, default: () => null },
    seriesStrokeColors: { type: Array, default: () => null },
    stacked: { type: Boolean, default: false },
    horizontal: { type: Boolean, default: false },
    xAxisTitle: { type: String, default: "" },
    yAxisTitle: { type: String, default: "" },
  },
  emits: ["pointSelect"],
  setup(props, { emit }) {
    const isCategorical = computed(() => isCategoricalType(props.type));

    const apexSeries = computed(() => {
      if (isCategorical.value) {
        // Donut/pie expect a flat number[] series.
        if (Array.isArray(props.series) && props.series.length && typeof props.series[0] === "object") {
          return props.series[0].data || [];
        }
        return props.series || [];
      }
      return props.series || [];
    });

    const isEmpty = computed(() => {
      if (isCategorical.value) {
        const arr = apexSeries.value;
        if (!arr || arr.length === 0) return true;
        return arr.every((n) => !n);
      }
      const arr = apexSeries.value;
      if (!arr || arr.length === 0) return true;
      return arr.every((s) => !s.data || s.data.length === 0 || s.data.every((v) => !v));
    });

    const palette = computed(() => props.colors || PALETTE);

    const numericMax = computed(() => {
      let max = 0;
      const list = Array.isArray(props.series) ? props.series : [];
      for (const s of list) {
        const data = (s && s.data) || (Array.isArray(s) ? s : []);
        for (const v of data) {
          if (typeof v === "number" && v > max) max = v;
        }
      }
      return Math.max(5, Math.ceil(max / 5) * 5);
    });

    const axisTitleStyle = {
      color: "#4a5568",
      fontSize: "12px",
      fontWeight: 600,
    };

    const integerFormatter = (val) => Math.round(Number(val) || 0).toString();

    const mergedOptions = computed(() => {
      const base = {
        chart: {
          fontFamily: "inherit",
          toolbar: { show: false },
          zoom: { enabled: false },
          stacked: props.stacked,
          animations: { enabled: true, easing: "easeinout", speed: 300 },
        },
        colors: palette.value,
        dataLabels: { enabled: false },
        legend: {
          show: true,
          position: "bottom",
          labels: { colors: "#4a5568" },
          markers: {
            width: 20,
            height: 20,
            radius: 6,
            customHTML: props.seriesStrokeColors
              ? props.seriesStrokeColors.map((stroke, i) => () => {
                  const fill = (props.colors && props.colors[i]) || PRIMARY;
                  const hasStroke = stroke && stroke !== "transparent";
                  return `<span style="display:block;width:100%;height:100%;border-radius:6px;background:${fill};${
                    hasStroke ? `border:2px solid ${stroke};` : ""
                  }box-sizing:border-box;"></span>`;
                })
              : undefined,
          },
        },
        grid: {
          borderColor: "#edf2f7",
          strokeDashArray: 4,
        },
        stroke: props.seriesStrokeColors
          ? {
              curve: "smooth",
              show: true,
              width: 2,
              colors: props.seriesStrokeColors,
            }
          : {
              curve: "smooth",
              width: props.type === "area" || props.type === "line" ? 2 : 0,
            },
        fill:
          props.type === "area"
            ? {
                type: "gradient",
                gradient: {
                  shadeIntensity: 1,
                  opacityFrom: 0.4,
                  opacityTo: 0.05,
                  stops: [0, 90, 100],
                },
              }
            : { type: "solid" },
        tooltip: {
          theme: "light",
          shared: true,
          intersect: false,
        },
      };

      if (isCategorical.value) {
        base.labels = props.categories || [];
        base.legend = { ...base.legend, position: "bottom" };
        base.plotOptions = {
          pie: { donut: { size: "65%" } },
        };
        // Donut/pie hover: halve the slice's alpha instead of applying
        // Apex's default darken filter, so the original colour is preserved.
        base.states = {
          hover: { filter: { type: "none" } },
          active: { filter: { type: "none" } },
        };
      } else {
        const numericAxisConfig = {
          min: 0,
          max: numericMax.value,
          tickAmount: 5,
          forceNiceScale: false,
          labels: {
            formatter: integerFormatter,
            style: { colors: "#718096", fontSize: "11px" },
          },
        };
        const categoricalAxisConfig = {
          labels: { style: { colors: "#718096", fontSize: "11px" } },
        };

        if (props.horizontal) {
          base.xaxis = {
            categories: props.categories || [],
            ...numericAxisConfig,
            axisBorder: { show: false },
            title: props.xAxisTitle
              ? { text: props.xAxisTitle, style: axisTitleStyle }
              : undefined,
          };
          base.yaxis = {
            ...categoricalAxisConfig,
            title: props.yAxisTitle
              ? { text: props.yAxisTitle, style: axisTitleStyle }
              : undefined,
          };
        } else {
          base.xaxis = {
            categories: props.categories || [],
            ...categoricalAxisConfig,
            axisBorder: { show: false },
            title: props.xAxisTitle
              ? { text: props.xAxisTitle, style: axisTitleStyle }
              : undefined,
          };
          base.yaxis = {
            ...numericAxisConfig,
            title: props.yAxisTitle
              ? { text: props.yAxisTitle, style: axisTitleStyle }
              : undefined,
          };
        }
      }

      if (props.type === "bar") {
        base.plotOptions = {
          ...base.plotOptions,
          bar: {
            borderRadius: 4,
            horizontal: props.horizontal,
            columnWidth: "55%",
          },
        };
      }

      if (typeof props.formatTooltip === "function") {
        base.tooltip = {
          ...base.tooltip,
          y: { formatter: props.formatTooltip },
        };
      }

      return base;
    });

    const onPointSelect = (_e, _ctx, config) => {
      if (!config) return;
      const seriesIndex = config.seriesIndex;
      const dataPointIndex = config.dataPointIndex;
      emit("pointSelect", {
        categoryIndex: dataPointIndex,
        seriesIndex,
        category: props.categories[dataPointIndex],
      });
    };

    return { apexSeries, mergedOptions, isEmpty, onPointSelect };
  },
};
</script>

<style scoped>
.base-chart {
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 18px 20px 8px;
  margin: 0;
  display: flex;
  flex-direction: column;
}

.base-chart-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.base-chart-heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.base-chart-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--lulu-text-strong);
  letter-spacing: 0.01em;
}

.base-chart-description {
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  color: var(--lulu-text-muted);
}

.base-chart-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.base-chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 32px 16px;
  color: var(--lulu-text-muted);
  gap: 8px;
}

.base-chart-empty-icon {
  color: var(--lulu-text-subtle);
  font-size: 32px !important;
}

.base-chart-empty-text {
  font-size: 13px;
  margin: 0;
}

.base-chart :deep(.apexcharts-pie-area) {
  transition: opacity 0.2s ease, fill-opacity 0.2s ease;
}

.base-chart :deep(.apexcharts-pie-area:hover) {
  opacity: 0.8 !important;
  fill-opacity: 0.8 !important;
}
</style>
